/**
 * Sidebar v2 thread list: the sortable pinned/active/snoozed/settled tree
 * with its drag context, section chrome, and per-row rendering.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (list rendering, ~lines 4460-4930) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: rows come from the controller's
 * overlay-aware map, forge's WorkerSubRow fan-out rides the card rows, and
 * the settle/unsettle/unsnooze per-section actions are the controller's.
 */
import { useState } from "react";
import { DndContext, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { PlusIcon } from "lucide-react";
import type { EnvironmentId } from "@cmmd-forge/contracts";

import { SidebarGroup } from "~/components/ui/sidebar";
import {
  snoozeWakeLabel as sharedSnoozeWakeLabel,
  threadWokeAt as sharedThreadWokeAt,
} from "./sidebarV2Settled";

import { SidebarV2Row } from "./sidebarV2Row";
import {
  SETTLED_TAIL_PAGE_COUNT,
  SidebarDragBoundary,
  SidebarSectionHeader,
  SidebarSectionPlaceholder,
  SortableThreadRow,
} from "./sidebarV2Chrome";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "./sidebarV2Drag";
import { useSidebarDragAdapters } from "./sidebarV2ThreadListDnd";
import { SidebarPointerSensor } from "./sidebarV2Pointer";
import { buildRowThreadProps } from "./sidebarV2RowThreadProps";
import type { ProjectDisplayEntry } from "../projectDisplayIndex";
import type { SavedEnvironmentRuntimeIndex } from "../sidebarThreadRuntimeIdentity";
import { resolveSidebarDropVerb, sidebarListItemId, type SidebarListItem } from "./sidebarV2List";
import type { useSidebarV2Controller } from "./sidebarV2Controller";

type Controller = ReturnType<typeof useSidebarV2Controller>;
type Section = "pinned" | "active" | "snoozed" | "settled";
type RowVariant = "card" | "slim";
type RowVariantAction = "unsnooze" | "unsettle" | "settle";

/** Shelf rows render slim with a wake action; inbox rows render as cards
 * with a settle action. */
function rowVariantFor(section: Section): {
  readonly variant: RowVariant;
  readonly variantAction: RowVariantAction;
} {
  if (section === "snoozed") return { variant: "slim", variantAction: "unsnooze" };
  if (section === "settled") return { variant: "slim", variantAction: "unsettle" };
  return { variant: "card", variantAction: "settle" };
}

/** The controller callbacks every row shares, resolved once per list. */
function buildRowCallbacks(controller: Controller) {
  return {
    onThreadClick: controller.handleThreadClick,
    onThreadActivate: controller.navigateToThread,
    onStartRename: controller.startThreadRename,
    onRenameTitleChange: controller.setRenamingTitle,
    onCommitRename: controller.commitRename,
    onCancelRename: controller.cancelThreadRename,
    onContextMenu: controller.handleThreadContextMenu,
    onSettle: controller.attemptSettle,
    onUnsettle: controller.attemptUnsettle,
    onSnooze: controller.snoozeThread,
    onUnsnooze: controller.unsnoozeThread,
    onUnpin: controller.unpinThread,
    onAcknowledgeWoke: controller.acknowledgeWoke,
    onOpenPullRequest: controller.openPullRequestExternal,
    onNavigateWorker: controller.navigateToWorker,
  };
}

/** The drag-reactive props: drop verb, hover target, wake stamp. */
function buildRowDragProps(
  controller: Controller,
  thread: NonNullable<ReturnType<Controller["threadByKey"]["get"]>>,
  threadKey: string,
  section: Section,
  showThreadJumpHints: boolean,
  jumpLabelByKey: ReadonlyMap<string, string>,
  routeThreadKey: string | null,
) {
  const dropVerb =
    controller.dragState?.activeKey === threadKey
      ? resolveSidebarDropVerb(
          controller.dragState.activeSection,
          controller.dragState.targetSection,
        )
      : null;
  return {
    dropVerb,
    dragOverPinned:
      controller.dragState?.activeKey === threadKey &&
      controller.dragState.targetSection === "pinned",
    snoozeWakeLabelText:
      section === "snoozed" && thread.snoozedUntil != null
        ? sharedSnoozeWakeLabel(thread.snoozedUntil, { now: new Date().toISOString() })
        : null,
    wokeAt: sharedThreadWokeAt(thread, { now: controller.snoozeNow }),
    isActive: routeThreadKey === threadKey,
    jumpLabel: showThreadJumpHints ? (jumpLabelByKey.get(threadKey) ?? null) : null,
  };
}

/** One list item: a section marker, or a sortable thread row. */
function ListItem(props: {
  readonly item: SidebarListItem;
  readonly controller: Controller;
  readonly listProps: {
    readonly projectDisplayNameByKey: ReadonlyMap<string, string>;
    readonly projectDisplayByKey: ReadonlyMap<string, ProjectDisplayEntry>;
    readonly savedEnvironmentRegistry: SavedEnvironmentRuntimeIndex;
    readonly resolveEnvironmentLabel: (environmentId: EnvironmentId) => string;
    readonly currentEnvironmentId?: EnvironmentId | null | undefined;
    readonly routeThreadKey: string | null;
    readonly showThreadJumpHints: boolean;
    readonly jumpLabelByKey: ReadonlyMap<string, string>;
  };
}) {
  const { item, controller } = props;
  if (item.kind === "marker") {
    return <MarkerItem item={item} controller={controller} />;
  }
  const thread = controller.threadByKey.get(item.key);
  if (thread === undefined) return null;
  const { variant, variantAction } = rowVariantFor(item.section);
  const threadProps = buildRowThreadProps(
    {
      projectDisplayNameByKey: props.listProps.projectDisplayNameByKey,
      projectDisplayByKey: props.listProps.projectDisplayByKey,
      savedEnvironmentRegistry: props.listProps.savedEnvironmentRegistry,
      resolveEnvironmentLabel: props.listProps.resolveEnvironmentLabel,
      currentEnvironmentId: props.listProps.currentEnvironmentId,
    },
    thread,
  );
  const dragProps = buildRowDragProps(
    controller,
    thread,
    item.key,
    item.section,
    props.listProps.showThreadJumpHints,
    props.listProps.jumpLabelByKey,
    props.listProps.routeThreadKey,
  );
  return (
    <SortableThreadRow
      key={item.key}
      id={item.key}
      disabled={controller.renamingThreadKey === item.key}
    >
      {(bag) => (
        <SidebarV2Row
          thread={thread}
          variant={variant}
          variantAction={variantAction}
          isPinned={thread.pinnedAt != null}
          dropVerb={dragProps.dropVerb}
          dragOverPinned={dragProps.dragOverPinned}
          snoozeWakeLabelText={dragProps.snoozeWakeLabelText}
          wokeAt={dragProps.wokeAt}
          isActive={dragProps.isActive}
          jumpLabel={dragProps.jumpLabel}
          isRenaming={controller.renamingThreadKey === item.key}
          renamingTitle={controller.renamingTitle}
          environmentLabel={threadProps.environmentLabel}
          environmentMachine={threadProps.threadOrigin}
          projectCwd={threadProps.projectCwd}
          projectCanonicalIdentity={threadProps.projectCanonicalIdentity}
          projectDisplayName={threadProps.projectDisplayName}
          threadOrigin={threadProps.threadOrigin}
          linkedPrUrl={threadProps.linkedPrUrl}
          timestampFormat={controller.timestampFormat}
          workerRows={controller.workerRows(item.key) ?? []}
          sortable={bag}
          {...buildRowCallbacks(controller)}
        />
      )}
    </SortableThreadRow>
  );
}

interface MarkerItemProps {
  readonly item: Extract<SidebarListItem, { readonly kind: "marker" }>;
  readonly controller: Controller;
}

/** The pinned and active drag boundaries, shown only while a drag is live. */
function MarkerBoundaries(props: {
  readonly marker: "pinned-header" | "pinned-divider";
  readonly controller: Controller;
}) {
  const target = props.marker === "pinned-header" ? "pinned" : "active";
  return (
    <SidebarDragBoundary
      key={props.marker}
      marker={props.marker}
      label={props.marker === "pinned-header" ? "Pinned" : "Active"}
      visible={props.controller.dragState !== null}
      isDropTarget={props.controller.dragState?.targetSection === target}
    />
  );
}

/** The snoozed shelf header: collapsed by default with a live count. */
function SnoozedHeaderItem(props: { readonly controller: Controller }) {
  const { controller } = props;
  return (
    <SidebarSectionHeader
      key="snoozed-header"
      marker="snoozed-header"
      className="mt-auto"
      label={
        controller.snoozedShelfExpanded
          ? "Snoozed"
          : `Snoozed (${controller.partition.snoozedThreads.length})`
      }
      toggle={{
        expanded: controller.snoozedShelfExpanded,
        onToggle: controller.toggleSnoozedShelf,
      }}
    />
  );
}

/** The settled shelf header: doubles as the settle drop target mid-drag. */
function SettledHeaderItem(props: { readonly controller: Controller }) {
  const { controller } = props;
  return (
    <SidebarSectionHeader
      key="settled-header"
      marker="settled-header"
      className={controller.partition.snoozedThreads.length === 0 ? "mt-auto" : ""}
      label={
        controller.settledShelfExpanded
          ? "Settled"
          : `Settled (${controller.partition.settledThreads.length})`
      }
      dragging={controller.dragState !== null}
      isDropTarget={controller.dragState?.targetSection === "settled"}
      toggle={{
        expanded: controller.settledShelfExpanded,
        onToggle: controller.toggleSettledShelf,
      }}
    />
  );
}

/** The empty Active or Settled target, measurable without resting space. */
function SectionPlaceholderItem(props: {
  readonly marker: "active-placeholder" | "settled-placeholder";
  readonly controller: Controller;
}) {
  const target = props.marker === "active-placeholder" ? "active" : "settled";
  return (
    <SidebarSectionPlaceholder
      key={props.marker}
      marker={props.marker}
      label={target === "active" ? "Active" : "Settled"}
      showHint={props.controller.dragState !== null}
      isDropTarget={props.controller.dragState?.targetSection === target}
    />
  );
}

/** Render one structural list marker. */
function MarkerItem(props: MarkerItemProps) {
  const { marker } = props.item;
  if (marker === "pinned-header" || marker === "pinned-divider") {
    return <MarkerBoundaries marker={marker} controller={props.controller} />;
  }
  if (marker === "active-placeholder" || marker === "settled-placeholder") {
    return <SectionPlaceholderItem marker={marker} controller={props.controller} />;
  }
  if (marker === "snoozed-header") {
    return <SnoozedHeaderItem controller={props.controller} />;
  }
  return <SettledHeaderItem controller={props.controller} />;
}

/** Render sortable thread shelves and section markers, forwarding drag events to the controller. */
export function SidebarV2ThreadList(props: {
  readonly controller: Controller;
  readonly projectDisplayNameByKey: ReadonlyMap<string, string>;
  readonly projectDisplayByKey: ReadonlyMap<string, ProjectDisplayEntry>;
  readonly savedEnvironmentRegistry: SavedEnvironmentRuntimeIndex;
  readonly resolveEnvironmentLabel: (environmentId: EnvironmentId) => string;
  readonly currentEnvironmentId?: EnvironmentId | null | undefined;
  readonly routeThreadKey: string | null;
  readonly showThreadJumpHints: boolean;
  readonly jumpLabelByKey: ReadonlyMap<string, string>;
}) {
  const { controller } = props;
  // The pointer Y at pickup seeds the collision detector's boundary
  // hysteresis for this drag session; the session key rebuilds the
  // detector so a previous drag's state can never leak into the next.
  const [activationY, setActivationY] = useState<number | null>(null);
  const dragSensors = useSensors(
    useSensor(SidebarPointerSensor, {
      distance: 6,
      onAttach: () => {},
      onFinish: () => {},
    }),
  );
  const sortableIds = controller.sidebarListItems.map(sidebarListItemId);
  const { sortingStrategy, collisionDetection } = useSidebarDragAdapters({
    sidebarListItems: controller.sidebarListItems,
    dragSessionKey: controller.dragState?.occurredAt ?? null,
    activationY,
    settledThreads: controller.partition.settledThreads,
    settledExpanded: controller.settledShelfExpanded,
    settledVisibleCount: controller.visibleSettledThreads.length,
    routeThreadKey: props.routeThreadKey,
    snoozedThreadCount: controller.partition.snoozedThreads.length,
  });

  return (
    <SidebarGroup className="min-h-0 flex-1" data-testid="sidebar-v2-thread-list">
      <DndContext
        sensors={dragSensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
        onDragStart={(event) => {
          const activator = event.activatorEvent;
          setActivationY(
            activator instanceof MouseEvent && Number.isFinite(activator.clientY)
              ? activator.clientY
              : null,
          );
          controller.handleThreadDragStart(String(event.active.id));
        }}
        onDragOver={(event) => {
          controller.handleThreadDragOver(
            String(event.active.id),
            event.over === null ? null : String(event.over.id),
          );
        }}
        onDragEnd={(event) => {
          controller.handleThreadDragEnd(
            String(event.active.id),
            event.over === null ? null : String(event.over.id),
          );
        }}
      >
        <SortableContext items={sortableIds} strategy={sortingStrategy}>
          <ul
            role="list"
            className="relative flex flex-1 flex-col gap-px"
            data-testid="sidebar-v2-list"
          >
            {controller.sidebarListItems.map((item) => (
              <ListItem
                key={sidebarListItemId(item)}
                item={item}
                controller={controller}
                listProps={{
                  projectDisplayNameByKey: props.projectDisplayNameByKey,
                  projectDisplayByKey: props.projectDisplayByKey,
                  savedEnvironmentRegistry: props.savedEnvironmentRegistry,
                  resolveEnvironmentLabel: props.resolveEnvironmentLabel,
                  currentEnvironmentId: props.currentEnvironmentId,
                  routeThreadKey: props.routeThreadKey,
                  showThreadJumpHints: props.showThreadJumpHints,
                  jumpLabelByKey: props.jumpLabelByKey,
                }}
              />
            ))}
            {controller.settledShelfExpanded && controller.hiddenSettledCount > 0 ? (
              <li className="list-none">
                <button
                  type="button"
                  onClick={controller.showMoreSettled}
                  className="flex h-9 w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-left text-sm text-sidebar-muted-foreground/55 hover:bg-sidebar-row-hover hover:text-sidebar-foreground"
                >
                  <PlusIcon aria-hidden className="size-4 shrink-0" />
                  Show {Math.min(controller.hiddenSettledCount, SETTLED_TAIL_PAGE_COUNT)} more
                </button>
              </li>
            ) : null}
          </ul>
        </SortableContext>
      </DndContext>
      {!controller.isSearchingThreads && controller.sidebarListItems.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-2 py-6 text-center text-xs text-muted-foreground/60">
          <span>No threads yet</span>
        </div>
      ) : null}
    </SidebarGroup>
  );
}
